import React, {createRef, useEffect, useMemo, useState} from 'react';
import cn from 'classnames';

import {EVideoProviders} from 'coreClasses/product/IProduct';
import {Breakpoint} from 'coreClasses/view/IViewportMgr';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useLockScroll, useSetUI, useUI} from 'coreHooks';
import {getAssetUrl} from 'coreUtils/adapters/sdk';

import {default as Video} from 'brandComponents/core/Video';
import {EProviders} from 'brandComponents/core/Video/IVideoProps';
import Picture from 'brandComponents/Picture';
import {CONTROLS_OPTIONS_PDP, PRODUCT_IMAGES_SETTINGS, VIDEO_OPTIONS_PDP} from 'brandConstants';

import {IProductImagesProps} from './IProductImages';

import styles from './ProductImages.module.scss';

// The grid is shown from `lg` (`ImagesCarousel` below).
const GRID_BREAKPOINTS: Breakpoint[] = ViewportMgr.getBreakpoints(true).slice(ViewportMgr.getBreakpoints(true).indexOf('lg'));

const mapPdpVideoProvider = (provider?: EVideoProviders): EProviders => {
	switch (provider) {
		case EVideoProviders.CLOUDFLARE_STREAM:
			return EProviders.CLOUDFLARE_STREAM;
		case EVideoProviders.VIMEO:
			return EProviders.VIMEO;
		case EVideoProviders.THRON:
		case EVideoProviders.THRON_ASSET:
			return EProviders.THRON;
		default:
			return EProviders.VIMEO;
	}
};

const ProductImages = ({product, viewType}: IProductImagesProps) => {
	const setUI = useSetUI();
	const isServerUI = useUI('isServer');
	const zoomUI = useUI('pdpZoom');
	const {lockScroll} = useLockScroll({restoreScrollPosition: true});
	const isZoomActive = zoomUI.isActive;
	const [elRefs, setElRefs] = useState([]);
	const productId = product.getProductId();
	const productVideos = useMemo(() => product.getVideos() || [], [productId]);
	const rawProductImages = product.getProductImages(viewType, PRODUCT_IMAGES_SETTINGS.productPage);
	const productImageLength = rawProductImages && rawProductImages.length;
	const isNewArrival = product.isNewArrival();
	const cursorSrc = getAssetUrl('static/img/plus.svg');

	useEffect(() => {
		return () => {
			lockScroll(false);
			setUI.setUI({pdpZoom: {isActive: false}});
		};
	}, [productId]);

	useEffect(() => {
		if (isZoomActive) {
			lockScroll(true);
		} else {
			lockScroll(false);
		}
	}, [isZoomActive]);

	let productImages = rawProductImages.map((image, index) => (
		<div
			className={cn(styles['product-images__wrapper'], {
				[styles['product-images__wrapper-new-arrival']]: isNewArrival,
			})}
			style={{cursor: `url(${cursorSrc}) 0 0, pointer`}}
			key={image.getUrl()}
		>
			<Picture
				image={image}
				ref={elRefs[index]}
				showSkeleton={!isServerUI}
				noResize={isZoomActive}
				onClick={() => {
					setUI.setUI({
						pdpZoom: {
							isActive: !isZoomActive,
							imageIndex: index,
							isSecondaryZoom: true,
						},
					});
				}}
				index={index}
				priority={PRODUCT_IMAGES_SETTINGS.preloadImages.pdp && index === 0 && GRID_BREAKPOINTS}
				showPlaceholder={PRODUCT_IMAGES_SETTINGS.ShowPlaceholder.pdp}
			/>
		</div>
	));

	productVideos.forEach((video) => {
		const index = video.position === null ? productImages.length : video.position;
		productImages.splice(
			index,
			0,
			<div className={styles['product-images__video']}>
				<Video
					key={video.videoId}
					provider={mapPdpVideoProvider(video.provider)}
					videoId={video.videoId}
					videoConfig={VIDEO_OPTIONS_PDP}
					controlsConfig={CONTROLS_OPTIONS_PDP}
				/>
			</div>,
		);
	});

	useEffect(() => {
		setElRefs((elRefs) =>
			Array(productImageLength)
				.fill({})
				.map((_, i) => elRefs[i] || createRef()),
		);
	}, [productImageLength]);

	return (
		<>
			<div
				className={cn(styles['product-images'], 'product-images', {
					[`${styles['product-images__zoom']} product-images__zoom`]: isZoomActive,
				})}
			>
				{productImages}
			</div>
		</>
	);
};

export default ProductImages;
